import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  CardActionArea,
  Button,
  Stack,
  CircularProgress,
  Alert,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from '@mui/material';
import {
  TableRestaurant as DineInIcon,
  ShoppingBag as PickupIcon,
  ArrowForwardIos as ArrowForwardIosIcon,
  RestaurantMenu as DefaultIcon,
  FileCopy as CopyIcon,
  Store as StoreIcon,
} from '@mui/icons-material';
import { useAppStore } from '../store/appStore';
import { productService } from '../services/productService';
import toast from 'react-hot-toast';

const Menu = () => {
  const navigate = useNavigate();
  const { branch: selectedBranch } = useAppStore();
  const [menuTypes, setMenuTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [copyMenuDialogOpen, setCopyMenuDialogOpen] = useState(false);
  const [copyMenuSource, setCopyMenuSource] = useState(null);
  const [copyMenuName, setCopyMenuName] = useState('');
  const [copyMenuTargetBranch, setCopyMenuTargetBranch] = useState('');
  const [copyingMenu, setCopyingMenu] = useState(false);
  const [availableBranches, setAvailableBranches] = useState([]);

  const branchLabel = selectedBranch?.name || 'All Outlets';

  useEffect(() => {
    const fetchMenuTypes = async () => {
      try {
        setLoading(true);
        setError('');

        const params = {};
        if (selectedBranch?.id && !selectedBranch?.isAllOutlets) {
          params.branch_id = selectedBranch.id;
        }

        const response = await productService.getMenus(params);

        if (response?.data?.success) {
          setMenuTypes(response.data.data || []);
        } else {
          setError(response?.data?.message || 'Failed to load menu types');
        }
      } catch (err) {
        console.error('Error fetching menu types:', err);
        setError('Failed to load menu types');
      } finally {
        setLoading(false);
      }
    };

    fetchMenuTypes();
  }, [selectedBranch]);

  const getMenuIcon = (menuName) => {
    const name = menuName.toLowerCase();
    if (name.includes('dine') || name.includes('table')) {
      return <DineInIcon sx={{ fontSize: 44, color: 'primary.main' }} />;
    } else if (name.includes('pickup') || name.includes('takeout')) {
      return <PickupIcon sx={{ fontSize: 44, color: 'primary.main' }} />;
    } else {
      return <DefaultIcon sx={{ fontSize: 44, color: 'primary.main' }} />;
    }
  };

  const getMenuDescription = (menuName) => {
    const name = menuName.toLowerCase();
    if (name.includes('dine') || name.includes('table')) {
      return 'Manage dine-in menus and table service flows.';
    } else if (name.includes('pickup') || name.includes('takeout')) {
      return 'Manage takeout items and express pickup menus.';
    } else {
      return `Manage ${menuName.toLowerCase()} menus and service flows.`;
    }
  };

  const fetchBranchesForCopy = async () => {
    try {
      // For now, we'll use a simple approach. In a real app, you'd have a branches service
      // For this demo, we'll assume branches are available or can be fetched
      // You might need to add a branches API endpoint
      setAvailableBranches([
        { id: 1, name: 'Main Branch' },
        { id: 2, name: 'Downtown Branch' },
        { id: 3, name: 'Airport Branch' },
      ]);
    } catch (err) {
      console.error('Fetch branches failed:', err);
    }
  };

  const openCopyMenuDialog = async (menu) => {
    setCopyMenuSource(menu);
    setCopyMenuName(`${menu.name} (Copy)`);
    setCopyMenuDialogOpen(true);
    await fetchBranchesForCopy();
  };

  const closeCopyMenuDialog = () => {
    setCopyMenuDialogOpen(false);
    setCopyMenuSource(null);
    setCopyMenuName('');
    setCopyMenuTargetBranch('');
  };

  const handleCopyMenu = async () => {
    if (!copyMenuTargetBranch || !copyMenuName.trim()) {
      toast.error('Please select a target branch and provide a menu name');
      return;
    }

    setCopyingMenu(true);
    try {
      const response = await productService.copyMenu(copyMenuSource.id, {
        branch: copyMenuTargetBranch,
        name: copyMenuName.trim(),
      });

      if (response?.data?.success) {
        toast.success(`Menu copied successfully with ${response.data.data.categories_count} categories and ${response.data.data.products_count} dishes`);
        closeCopyMenuDialog();
        // Refresh menu types
        const params = {};
        if (selectedBranch?.id && !selectedBranch?.isAllOutlets) {
          params.branch_id = selectedBranch.id;
        }
        const refreshResponse = await productService.getMenus(params);
        if (refreshResponse?.data?.success) {
          setMenuTypes(refreshResponse.data.data || []);
        }
      } else {
        toast.error(response?.data?.message || 'Failed to copy menu');
      }
    } catch (err) {
      console.error('Copy menu error:', err);
      toast.error('Failed to copy menu');
    } finally {
      setCopyingMenu(false);
    }
  };

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'space-between', gap: 2, mb: 3, alignItems: 'center' }}>
        <Box sx={{ minWidth: 320 }}>
          <Typography variant="h4" sx={{ fontWeight: 700, mb: 1 }}>
            Menu Management
          </Typography>
          <Typography color="text.secondary" sx={{ maxWidth: 620 }}>
            Choose a menu type to manage your restaurant menu. Each flow opens a category-driven menu builder with actions for adding categories and dishes.
          </Typography>
          <Typography variant="body2" sx={{ mt: 1, color: 'text.secondary' }}>
            Current branch: <strong>{branchLabel}</strong>
          </Typography>
        </Box>
        <Button
          variant="outlined"
          endIcon={<ArrowForwardIosIcon />}
          onClick={() => navigate('/dish-access/dine-in')}
          sx={{ whiteSpace: 'nowrap' }}
        >
          Start with Dine In
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 300 }}>
          <CircularProgress />
        </Box>
      ) : (
        <Grid container spacing={3}>
          {menuTypes
            .filter(menu => menu.is_active)
            .map((menu) => (
            <Grid item xs={12} sm={6} md={4} key={menu.id}>
              <Card
                component={CardActionArea}
                onClick={() => navigate(`/dish-access/${menu.id}`)}
                sx={{
                  height: '100%',
                  borderRadius: 3,
                  transition: 'transform 200ms ease, box-shadow 200ms ease',
                  '&:hover': {
                    transform: 'translateY(-4px)',
                    boxShadow: 4,
                  },
                }}
              >
                <CardContent>
                  <Stack direction="row" alignItems="center" spacing={3}>
                    <Box>{getMenuIcon(menu.name)}</Box>
                    <Box sx={{ flex: 1 }}>
                      <Typography variant="h5" sx={{ fontWeight: 700, mb: 1 }}>
                        {menu.name}
                      </Typography>
                      <Typography color="text.secondary">
                        {getMenuDescription(menu.name)}
                      </Typography>
                    </Box>
                  </Stack>

                  <Box sx={{ mt: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Tooltip title="Copy Menu to Another Branch">
                      <IconButton
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          openCopyMenuDialog(menu);
                        }}
                        sx={{
                          color: 'primary.main',
                          '&:hover': {
                            backgroundColor: 'primary.light',
                            color: 'primary.contrastText',
                          },
                        }}
                      >
                        <CopyIcon />
                      </IconButton>
                    </Tooltip>
                    <Button endIcon={<ArrowForwardIosIcon />} variant="contained">
                      Manage {menu.name}
                    </Button>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}

      {!loading && menuTypes.filter(menu => menu.is_active).length === 0 && !error && (
        <Box sx={{ textAlign: 'center', py: 8 }}>
          <Typography variant="h6" color="text.secondary">
            No menu types available for the selected branch.
          </Typography>
        </Box>
      )}

      {/* Copy Menu Dialog */}
      <Dialog
        open={copyMenuDialogOpen}
        onClose={closeCopyMenuDialog}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            boxShadow: '0 20px 60px rgba(0,0,0,0.15)'
          }
        }}
      >
        <DialogTitle sx={{
          backgroundColor: '#F5F5F5',
          color: '#37474F',
          fontWeight: 600,
          fontSize: '1.25rem',
          pb: 2,
          borderBottom: '1px solid #E0E0E0'
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <CopyIcon sx={{ color: '#1976D2' }} />
            Copy Menu to Another Branch
          </Box>
        </DialogTitle>
        <DialogContent sx={{ p: 3, maxHeight: 'calc(100vh - 250px)', overflow: 'auto' }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, mt: 1 }}>
            {copyMenuSource && (
              <Alert
                severity="info"
                sx={{
                  borderRadius: 2,
                  backgroundColor: '#E3F2FD',
                  color: '#0D47A1',
                  '& .MuiAlert-icon': {
                    color: '#0D47A1',
                  },
                }}
              >
                <strong>Source Menu:</strong> {copyMenuSource.name}
              </Alert>
            )}

            <TextField
              label="New Menu Name"
              value={copyMenuName}
              onChange={(e) => setCopyMenuName(e.target.value)}
              placeholder="e.g., Dine-in (Downtown)"
              fullWidth
              variant="outlined"
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: 2,
                  '&:hover fieldset': {
                    borderColor: '#1976D2',
                  },
                  '&.Mui-focused fieldset': {
                    borderColor: '#1976D2',
                  },
                },
                '& .MuiInputLabel-root.Mui-focused': {
                  color: '#1976D2',
                },
              }}
            />

            <FormControl fullWidth variant="outlined">
              <InputLabel sx={{ color: '#607D8B', '&.Mui-focused': { color: '#1976D2' } }}>
                Target Branch
              </InputLabel>
              <Select
                value={copyMenuTargetBranch}
                onChange={(e) => setCopyMenuTargetBranch(e.target.value)}
                label="Target Branch"
                sx={{
                  borderRadius: 2,
                  '& .MuiOutlinedInput-notchedOutline': {
                    borderColor: '#E0E0E0',
                  },
                  '&:hover .MuiOutlinedInput-notchedOutline': {
                    borderColor: '#1976D2',
                  },
                  '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                    borderColor: '#1976D2',
                  },
                }}
              >
                {availableBranches.map((branch) => (
                  <MenuItem key={branch.id} value={branch.id}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <StoreIcon sx={{ fontSize: 18, color: '#607D8B' }} />
                      {branch.name}
                    </Box>
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <Alert
              severity="info"
              sx={{
                borderRadius: 2,
                backgroundColor: '#E3F2FD',
                color: '#0D47A1',
                '& .MuiAlert-icon': {
                  color: '#0D47A1',
                },
              }}
            >
              This will copy the entire menu structure including all categories and dishes to the selected branch.
            </Alert>
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 3, pt: 0, gap: 2 }}>
          <Button
            onClick={closeCopyMenuDialog}
            sx={{
              color: '#607D8B',
              fontWeight: 500,
              textTransform: 'none',
              px: 3,
              '&:hover': {
                backgroundColor: '#F5F5F5',
              },
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleCopyMenu}
            variant="contained"
            disabled={copyingMenu || !copyMenuTargetBranch || !copyMenuName.trim()}
            sx={{
              backgroundColor: '#1976D2',
              color: 'white',
              fontWeight: 500,
              textTransform: 'none',
              px: 3,
              py: 1.5,
              borderRadius: 2,
              boxShadow: '0 2px 8px rgba(25,118,210,0.3)',
              '&:hover': {
                backgroundColor: '#1565C0',
                boxShadow: '0 4px 12px rgba(25,118,210,0.4)',
              },
              '&:disabled': {
                backgroundColor: '#BDBDBD',
                color: '#9E9E9E',
              },
              transition: 'all 0.2s ease-in-out',
            }}
          >
            {copyingMenu ? (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <CircularProgress size={16} color="inherit" />
                Copying...
              </Box>
            ) : (
              'Copy Menu'
            )}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Menu;
